import type { GameEndReason } from '@chess-uno/shared-protocol'
import type { Messages } from './fr.js'

/** A counted word, Hindi rule: the plural form only beyond 1 ("1 चाल", "0 चालें", "3 चालें"). */
function n(count: number, word: string, plural = word): string {
  return `${count} ${count === 1 ? word : plural}`
}

/** "1 चाल", "3 चालें". */
function moves(count: number): string {
  return n(count, 'चाल', 'चालें')
}

/** "1 प्यादा", "3 प्यादे". */
function pawns(count: number): string {
  return n(count, 'प्यादा', 'प्यादे')
}

export const hi: Messages = {
  common: {
    back: 'वापस',
    home: 'होम',
    cancel: 'रद्द करें',
    loading: 'लोड हो रहा है…',
    error: 'गड़बड़ी',
    yes: 'हाँ',
    no: 'नहीं',
    armies: { w: 'सफ़ेद', b: 'काले' },
    tiers: { bronze: 'ब्रॉन्ज़', silver: 'सिल्वर', gold: 'गोल्ड', platinum: 'प्लैटिनम', diamond: 'डायमंड' },
  },

  modes: {
    purist: {
      name: 'शुद्ध',
      tagline: 'शह और मात',
      desc: 'बिल्कुल शतरंज जैसा: राजा पर शह कभी नहीं छोड़ी जाती, राजा को मारा नहीं जाता, जीत शह और मात से होती है।',
      note: 'शुद्ध मोड: आप अपने राजा पर शह नहीं छोड़ सकते; जीत शह और मात से।',
    },
    capture: {
      name: 'राजा का शिकार',
      tagline: 'राजा को मारो',
      desc: 'मूल नियम: राजा पर हमला रहने दिया जा सकता है, और जीत विरोधी राजा को मारकर होती है।',
      note: 'राजा का शिकार मोड: राजा पर हमला रह सकता है; जीत उसे मारकर होती है।',
    },
  },

  campaign: {
    eyebrow: 'नवंबर चैलेंज',
    title: 'Beat the machine',
    text: 'मध्यम लेवल पर कंप्यूटर को हराएँ और अपनी जीत Instagram पर शेयर करें: हर सोमवार €20 का गिफ़्ट कार्ड ड्रॉ होता है।',
    link: 'पूरी जानकारी Instagram पर',
    dismiss: 'छिपाएँ',
  },

  language: {
    names: { fr: 'Français', en: 'English', es: 'Español', de: 'Deutsch', hi: 'हिन्दी' },
    menu: 'भाषा',
    hint: 'यह साइट हिन्दी में भी उपलब्ध है।',
    hintLink: 'हिन्दी संस्करण',
    dismiss: 'बंद करें',
  },

  chrome: {
    homeAria: 'Chess-Uno, होम',
    rules: 'नियम',
    viewProfile: 'मेरी प्रोफ़ाइल देखें',
    soundOn: 'आवाज़ चालू करें',
    soundOff: 'आवाज़ बंद करें',
    logout: 'लॉग आउट',
    legal: 'कानूनी जानकारी',
    privacy: 'प्राइवेसी',
    instagram: 'Instagram पर Chess-Uno',
    facebook: 'Facebook पर Chess-Uno',
  },

  feedback: {
    link: 'फ़ीडबैक',
    title: 'अपनी राय बताइए',
    lead: 'कोई आइडिया, कोई बग, या तारीफ़? बस एक मैसेज भेजिए: गेम के पब्लिशर उसे खुद पढ़ते हैं।',
    close: 'बंद करें',
    kindLabel: 'विषय',
    kinds: { idea: 'आइडिया', bug: 'बग', praise: 'पसंद आया', other: 'अन्य' },
    messageLabel: 'आपका मैसेज',
    placeholders: {
      idea: 'आप Chess-Uno में क्या देखना चाहेंगे?',
      bug: 'क्या हुआ? किस पेज पर, किस डिवाइस पर?',
      praise: 'आपको सबसे ज़्यादा क्या पसंद है?',
      other: 'जो भी कहना चाहें, लिखिए।',
    },
    contactLabel: 'ताकि हम जवाब दे सकें (वैकल्पिक)',
    contactPlaceholder: 'ईमेल या Telegram यूज़रनेम',
    fine: (privacy) => (
      <>
        आपका मैसेज पब्लिशर पढ़ते हैं, और अगर आप लॉग इन हैं तो साथ में आपका यूज़रनेम भी ({privacy('प्राइवेसी पॉलिसी')})।
      </>
    ),
    send: 'भेजें',
    sending: 'भेजा जा रहा है…',
    thanksTitle: 'धन्यवाद!',
    thanksText: 'आपका मैसेज मिल गया। इससे गेम को बेहतर बनाने में मदद मिलती है।',
    rateGame: 'क्या आपको यह गेम पसंद आया?',
    starLabel: (count) => `5 में से ${count}`,
    rated: 'रेटिंग देने के लिए धन्यवाद!',
    tellMore: 'वजह बताइए',
  },

  toast: {
    aria: (name) => `${name} की चुनौती`,
    challengesYou: (name) => (
      <>
        <strong>{name}</strong> ने आपको चुनौती दी है
      </>
    ),
    accept: 'चुनौती स्वीकार करें',
    later: 'बाद में',
  },

  auth: {
    login: 'लॉग इन',
    register: 'अकाउंट बनाएँ',
    username: 'यूज़रनेम',
    password: 'पासवर्ड',
    registerFine: (privacy) => (
      <>
        यूज़रनेम 3 से 20 कैरेक्टर का, पासवर्ड कम से कम 6 कैरेक्टर का। आपका यूज़रनेम और आपके नतीजे सार्वजनिक रैंकिंग में दिखते हैं (
        {privacy('प्राइवेसी')})।
      </>
    ),
    submitRegister: 'मेरा अकाउंट बनाएँ',
    submitLogin: 'लॉग इन करें',
  },

  landing: {
    eyebrow: 'Chess-Uno · ऑनलाइन शतरंज और कार्ड',
    title: 'कार्डों के साथ शतरंज खेलिए।',
    titleEm: 'हर बारी गेम बदल देती है।',
    lead:
      'Chess-Uno में आप शतरंज खेलते हैं, पर गेम के दौरान खुलने वाले कार्डों के साथ। हर बारी आपके विकल्प बदल देती है: चालों की संख्या, मिलने वाले प्यादे या आपके हाथ में कौन-सी सेना है। शह और मात देकर जीतिए, या राजा का शिकार मोड में विरोधी राजा को मारकर। किसी दोस्त को चुनौती दीजिए, रैंक्ड विरोधी ढूँढिए या कंप्यूटर के खिलाफ़ प्रैक्टिस कीजिए।',
    readRules: 'नियम पढ़ें',
    leaderboard: 'रैंकिंग',
    howEyebrow: 'कैसे खेलें',
    howTitle: 'शतरंज का गेम, ऐसे कार्डों के साथ जो चालें बदल देते हैं',
    howP1:
      'बोर्ड और मोहरे शतरंज की तरह ही चलते हैं। हर बारी की शुरुआत में एक कार्ड खींचा जाता है और सबको दिखाया जाता है: वह कई चालें दे सकता है, बारी छुड़वा सकता है, प्यादे वापस दिला सकता है या सेनाएँ बदल सकता है। दो मोड हैं: शुद्ध, जिसमें जीत शह और मात से होती है, और राजा का शिकार, जिसमें जीत विरोधी राजा को मारकर होती है।',
    howP2:
      'आपके हाथ में कोई कार्ड नहीं रहता। नंबर कार्ड आपको उतनी चालें देता है जितना उसका नंबर है; पहली चाल के बाद आप यह सीरीज़ रोक सकते हैं। +2 और +4 कार्ड से मारे गए प्यादे वापस बोर्ड पर रखे जा सकते हैं। रिवर्स से खिलाड़ी अपनी सेनाएँ आपस में बदल लेते हैं, मोहरों की जगह वही रहती है। जोकर से उस बारी में कोई एक मोहरा वज़ीर की तरह चल सकता है। खुला हुआ कार्ड दोनों खिलाड़ियों को हमेशा दिखता है।',
    discoverRules: 'Chess-Uno के नियम जानें',
    playEyebrow: 'ऑनलाइन खेलें',
    playTitle: 'दोस्तों के साथ, रैंक्ड गेम में या कंप्यूटर के खिलाफ़',
    playP1:
      'प्राइवेट मुकाबला बनाइए और उसका लिंक शेयर कीजिए, Elo वाले रैंक्ड गेम के लिए कतार में लगिए, या कंप्यूटर के खिलाफ़ कार्ड आज़माइए। कंप्यूटर के खिलाफ़ खेले गए गेम से आपकी रेटिंग नहीं बदलती।',
    playP2:
      'अकाउंट बनाने के बाद आप प्राइवेट लिंक से किसी दोस्त को बुला सकते हैं या अपने-आप कोई विरोधी ढूँढ सकते हैं। दो अकाउंट के बीच पूरे हुए गेम से आपका Elo बदलता है; रैंकिंग में हाल के नतीजे और आपके आमने-सामने के रिकॉर्ड भी दिखते हैं। बिना किसी दाँव के कार्डों के असर समझने के लिए कंप्यूटर के खिलाफ़ प्रैक्टिस गेम शुरू कीजिए। प्राइवेट मुकाबले से पहले आप चुन सकते हैं कि गड्डी में कौन-से कार्ड हों और नंबर कार्ड का सबसे बड़ा नंबर क्या हो।',
    heroAlt: 'कार्डों के सामने शतरंज का एक प्यादा',
    faqEyebrow: 'अक्सर पूछे जाने वाले सवाल',
    faqTitle: 'Chess-Uno के बारे में सब कुछ',
    faq: [
      {
        q: 'Chess-Uno क्या है?',
        a: 'Chess-Uno शतरंज का एक वेरिएंट है जिसे आप ब्राउज़र में मुफ़्त में ऑनलाइन खेल सकते हैं। इसमें आम शतरंज बोर्ड और वही मोहरे होते हैं, लेकिन हर बारी की शुरुआत में एक कार्ड खींचा जाता है और दोनों खिलाड़ियों को दिखाया जाता है: वही तय करता है कि उस बारी में खिलाड़ी क्या कर सकता है।',
      },
      {
        q: 'गेम कैसे जीतते हैं?',
        a: 'यह गेम से पहले चुने गए मोड पर निर्भर करता है। शुद्ध मोड में, शतरंज की तरह, आप अपने राजा पर कभी शह नहीं छोड़ सकते और जीत शह और मात से होती है: जब विरोधी अभी-अभी खींचे गए कार्ड से शह का बचाव नहीं कर पाता। राजा का शिकार मोड में आप अपने राजा पर हमला रहने दे सकते हैं, और जीत विरोधी राजा को मारकर होती है।',
      },
      {
        q: 'कार्ड क्या करते हैं?',
        a: 'नंबर कार्ड उतनी चालें देता है जितना उसका नंबर है (डिफ़ॉल्ट रूप से 1 से 5)। स्किप से खिलाड़ी की बारी चली जाती है। +2 और +4 कार्ड मारे गए प्यादे वापस दिलाते हैं, जिन्हें बोर्ड पर रखा जाता है। रिवर्स दोनों खिलाड़ियों की सेनाएँ आपस में बदल देता है। जोकर से कोई एक मोहरा वज़ीर की तरह चल सकता है। किसी भी खिलाड़ी के हाथ में कार्ड नहीं रहते।',
      },
      {
        q: 'क्या मैं अकेले खेल सकता हूँ?',
        a: 'हाँ, कंप्यूटर के खिलाफ़, आसान या मध्यम लेवल पर। ये प्रैक्टिस गेम रैंकिंग में नहीं गिने जाते। आप प्राइवेट लिंक से किसी दोस्त को चुनौती भी दे सकते हैं या रैंक्ड गेम की कतार में कोई विरोधी ढूँढ सकते हैं।',
      },
      {
        q: 'रैंकिंग कैसे काम करती है?',
        a: 'हर खिलाड़ी 1000 Elo से शुरू करता है। सिर्फ़ दो अकाउंट के बीच पूरे हुए गेम ही रैंक होते हैं। टियर ब्रॉन्ज़ से डायमंड (1400 Elo और उससे ऊपर) तक जाते हैं, और खिलाड़ियों के आमने-सामने के रिकॉर्ड सार्वजनिक होते हैं।',
      },
      {
        q: 'क्या कुछ पैसे देने या इंस्टॉल करने की ज़रूरत है?',
        a: 'नहीं। Chess-Uno मुफ़्त है और ब्राउज़र में चलता है, कंप्यूटर और मोबाइल दोनों पर। बस एक यूज़रनेम और पासवर्ड से अकाउंट बनाना होता है, ईमेल पते की ज़रूरत नहीं।',
      },
      {
        q: 'क्या Chess-Uno का UNO गेम से कोई संबंध है?',
        a: 'नहीं। Chess-Uno एक स्वतंत्र गेम है; यह UNO ट्रेडमार्क की मालिक कंपनी Mattel से न तो जुड़ा है, न उसके द्वारा समर्थित है और न ही प्रायोजित।',
      },
    ],
  },

  home: {
    greeting: (hour) => (hour >= 5 && hour < 12 ? 'सुप्रभात' : hour >= 12 && hour < 18 ? 'नमस्ते' : 'शुभ संध्या'),
    play: 'खेलें',
    ranked: 'रैंक्ड गेम',
    rankedDetail: 'विरोधी अपने-आप मिलेगा',
    challengeFriend: 'दोस्त को चुनौती दें',
    challengeFriendDetail: 'शेयर करने के लिए प्राइवेट लिंक',
    join: 'जुड़ें',
    joinDetail: 'इनवाइट कोड से',
    training: 'प्रैक्टिस',
    trainingDetail: 'AI के खिलाफ़, अनरैंक्ड',
    tournament: 'टूर्नामेंट',
    tournamentDetail: 'नॉकआउट, कोड के साथ',
    tournamentOngoing: (name) => `टूर्नामेंट जारी है: ${name}`,
    tournamentOpen: (name) => `रजिस्ट्रेशन खुला है: ${name}`,
    rules: 'गेम के नियम',
    waitingGame: 'गेम विरोधी का इंतज़ार कर रहा है',
    ongoingAgainst: (name) => `${name} के खिलाफ़ गेम जारी है`,
    resume: 'जारी रखें',
    leaderboard: 'कुल रैंकिंग',
    recentResults: 'ताज़ा नतीजे',
    headToHead: 'आमने-सामने',
    rivalries: 'बड़ी टक्करें',
  },

  join: {
    eyebrow: 'न्योता',
    title: 'गेम से जुड़ें',
    lead: 'आपके विरोधी ने जो कोड भेजा है, उसे डालें।',
    code: 'इनवाइट कोड',
    submit: 'गेम से जुड़ें',
  },

  invite: {
    challengesYou: (name) => (
      <>
        <strong>{name}</strong> ने आपको चुनौती दी है
      </>
    ),
    invited: 'आपको एक मुकाबले का न्योता मिला है',
  },

  gate: {
    title: 'लॉग इन',
    lead: 'खेलने और रैंकिंग में ऊपर चढ़ने के लिए लॉग इन करें।',
  },

  queue: {
    searching: 'विरोधी ढूँढा जा रहा है…',
    chooseTitle: 'अपना मोड चुनें',
    chooseLead: 'आपका मुकाबला ऐसे खिलाड़ी से होगा जिसने यही मोड चुना है। Elo रेटिंग दोनों मोड में एक ही है।',
    changeMode: 'मोड बदलें',
    eyebrow: 'रैंक्ड गेम',
    lead: 'जैसे ही कोई खिलाड़ी कतार में आएगा, गेम शुरू हो जाएगा।',
  },

  competition: {
    justNow: 'अभी-अभी',
    minutesAgo: (m) => `${m} मिनट पहले`,
    hoursAgo: (h) => `${h} घंटे पहले`,
    daysAgo: (d) => `${d} दिन पहले`,
    tierTitle: (label) => `${label} टियर`,
    streakTitle: (count) => `लगातार ${n(count, 'जीत')}`,
    form: { W: 'जीत', L: 'हार', D: 'ड्रॉ' },
    formAria: (items) => `फ़ॉर्म: ${items.join(', ')}`,
    yourRanking: 'आपकी रैंकिंग',
    outOf: (count) => `${count} ${count === 1 ? 'रैंक्ड खिलाड़ी' : 'रैंक्ड खिलाड़ियों'} में से`,
    unranked: 'अनरैंक्ड — कोई रैंक्ड गेम खेलें',
    pointsFor: (missing, label) => `${label} के लिए ${missing} पॉइंट और`,
    maxTier: 'सबसे ऊँचा टियर',
    pointsBefore: (missing, label) => (
      <>
        {label} तक <b>{missing}</b> पॉइंट
      </>
    ),
    maxTierCaption: 'सबसे ऊँचा टियर — अपनी जगह बचाए रखें',
    wins: 'जीत',
    losses: 'हार',
    draws: 'ड्रॉ',
    winRate: 'जीत का प्रतिशत',
    percent: (value) => `${value}%`,
    streak: 'लगातार जीत',
    best: (count) => `रिकॉर्ड ${count}`,
    leaderboardEmpty: 'अभी तक कोई रैंक नहीं हुआ है। रैंकिंग शुरू करने के लिए किसी दोस्त के खिलाफ़ एक गेम जीतिए।',
    you: 'आप',
    record: (w, l, d) => `${w} जीत · ${l} हार${d ? ` · ${d} ड्रॉ` : ''}`,
    challenge: 'चुनौती दें',
    taunts: {
      never: 'कभी आमना-सामना नहीं हुआ: स्कोर की शुरुआत आप कीजिए।',
      twoLosses: 'लगातार दो हार। अब बदला लेने का वक्त है।',
      dominate: 'इस मुकाबले में आपका दबदबा है।',
      lead: 'आप आगे हैं, पर अभी कुछ तय नहीं।',
      tied: 'बिल्कुल बराबरी।',
      escape: 'यह मुकाबला आपके हाथ से फिसल रहा है।',
      behind: 'आप पीछे हैं।',
    },
    rivalsEmpty: 'अभी कोई आमना-सामना नहीं हुआ। रैंकिंग से किसी खिलाड़ी को चुनौती दीजिए।',
    revenge: 'बदला लें',
    replay: 'फिर से खेलें',
    drawsShort: (d) => `${d} ड्रॉ`,
    rivalriesEmpty: 'जब दो खिलाड़ी दो बार आमने-सामने हो जाते हैं, तब टक्कर यहाँ दिखती है।',
    rivalryMeta: (games, draws) => `${n(games, 'गेम')}${draws ? ` · ${n(draws, 'ड्रॉ')}` : ''}`,
    recentEmpty: 'अभी तक कोई रैंक्ड गेम नहीं हुआ।',
    and: 'और',
    drew: 'ने ड्रॉ खेला',
    beat: 'ने हराया',
    reasons: {
      king_captured: 'राजा मारा गया',
      checkmate: 'शह और मात',
      resign: 'हार मान ली',
      abandon: 'डिस्कनेक्ट',
      draw_agreed: 'सहमति से ड्रॉ',
      no_move_limit: 'निष्क्रियता से ड्रॉ',
    },
  },

  settings: {
    decrease: 'घटाएँ',
    increase: 'बढ़ाएँ',
    maxNumberAria: 'नंबर कार्ड का सबसे बड़ा नंबर',
    keepCards: (max) => `1 से ${max} तक के कार्ड रखें`,
    eyebrowBot: 'प्रैक्टिस · अनरैंक्ड',
    eyebrowPrivate: 'प्राइवेट गेम · रैंक्ड',
    title: 'गेम की सेटिंग',
    leadBot: 'विरोधी और गड्डी सेट करें, फिर गेम शुरू करें।',
    leadPrivate: 'गड्डी सेट करें; आपके विरोधी के लिए एक इनवाइट लिंक बनाया जाएगा।',
    opponent: 'विरोधी',
    opponentHint: 'प्रयोग वाले लेवल: AI को सोचने के लिए कम या ज़्यादा समय मिलता है।',
    easy: 'आसान',
    easyDesc: 'विरोधी की एक चाल आगे तक देखता है।',
    medium: 'मध्यम',
    mediumDesc: 'दो चालें आगे तक देखता है, ज़्यादा देर सोचता है।',
    side: 'आपकी तरफ़',
    sideHint: 'गेम के दौरान रिवर्स कार्ड सेनाएँ आपस में बदल सकता है।',
    whiteDesc: 'पहली चाल आपकी',
    blackDesc: 'पहली चाल AI की',
    random: 'रैंडम',
    randomSummary: 'रैंडम ड्रॉ',
    rules: 'नियम',
    rulesHint: 'खेलने के दो तरीके: शतरंज की तरह, या राजा का शिकार करके।',
    deck: 'गड्डी',
    deckHint: 'नंबर कार्ड उतनी चालें देता है जितना उसका नंबर है।',
    numberCards: 'नंबर कार्ड',
    fromOneTo: (max) => (
      <>
        1 से <b>{max}</b> तक
      </>
    ),
    jokers: 'जोकर',
    jokersDesc: 'एक चाल, जिसमें कोई भी मोहरा वज़ीर की तरह चलता है।',
    advanced: 'एडवांस्ड नियम',
    advancedHint: 'प्यादों की वापसी, सुरक्षा नियम, ड्रॉ।',
    plus2: '+2 से वापस मिलने वाले प्यादे',
    plus4: '+4 से वापस मिलने वाले प्यादे',
    drawAfter: 'N बारियों तक कोई चाल न हो तो ड्रॉ',
    safeguard: 'सीरीज़ का सुरक्षा नियम',
    safeguardDesc: 'विरोधी राजा पर हमला होते ही सीरीज़ रुक जाती है।',
    summary: 'सारांश',
    mode: 'मोड',
    modeBot: 'AI के खिलाफ़',
    modePrivate: 'प्राइवेट लिंक',
    level: 'लेवल',
    camp: 'तरफ़',
    deckSize: (count) => n(count, 'कार्ड'),
    numbers: 'नंबर',
    range: (max) => `1 से ${max}`,
    jokersIncluded: 'जोकर',
    ranking: 'रैंकिंग',
    unranked: 'अनरैंक्ड',
    eloAtStake: 'Elo दाँव पर',
    start: 'गेम शुरू करें',
    create: 'गेम बनाएँ',
    locked: 'गेम शुरू होते ही नियम लॉक हो जाते हैं।',
  },

  game: {
    drawAgreed: 'ड्रॉ (आपसी सहमति से)।',
    drawIdle: 'ड्रॉ (बिना चाल की बारियाँ)।',
    result: (mine, winner, reason) => {
      const why: Partial<Record<GameEndReason, string>> = {
        king_captured: 'राजा को मारकर',
        checkmate: 'शह और मात से',
        resign: 'विरोधी के हार मानने से',
        abandon: 'विरोधी के लंबे समय तक डिस्कनेक्ट रहने से',
      }
      return `${mine ? 'जीत' : 'हार'} — ${winner} ने ${why[reason] ?? reason} गेम जीता।`
    },
    headToHead: 'आमने-सामने',
    firstDuel: 'पहला मुकाबला',
    youLead: 'आप आगे हैं',
    leads: (name) => `${name} आगे हैं`,
    tied: 'बराबरी',
    training: (level) => `AI (${level} लेवल) के खिलाफ़ प्रैक्टिस गेम: अनरैंक्ड।`,
    computing: 'रैंकिंग की गणना हो रही है…',
    promotion: (tier) => `प्रमोशन: ${tier} टियर`,
    demotion: (tier) => `${tier} में नीचे आ गए… अब बदला लेने का वक्त है।`,
    recordAgainst: (name) => `${name} के खिलाफ़ रिकॉर्ड:`,
    streak: (count) => `लगातार ${n(count, 'जीत')}`,
    backHome: 'होम पर वापस',
    privateGame: 'प्राइवेट गेम',
    waiting: 'विरोधी का इंतज़ार…',
    shareLink: 'यह लिंक शेयर करें:',
    copied: 'कॉपी हो गया!',
    copyLink: 'लिंक कॉपी करें',
    inviteText: (name) => `${name} ने तुम्हें Chess-Uno पर चुनौती दी है! मुकाबला स्वीकार करो:`,
    messageCopied: 'मैसेज कॉपी हो गया!',
    sent: 'भेज दिया!',
    sendChallenge: 'चुनौती भेजें',
    code: 'कोड:',
    notified: 'अगर चुनौती पाने वाला खिलाड़ी ऑनलाइन है, तो उसे नोटिफ़िकेशन भी मिलेगा।',
    loadingGame: 'गेम लोड हो रहा है…',
    connecting: '(सर्वर से कनेक्ट हो रहा है…)',
    connected: 'कनेक्टेड',
    reconnecting: 'फिर से कनेक्ट हो रहा है…',
    acceptDraw: 'ड्रॉ स्वीकार करें',
    decline: 'ठुकराएँ',
    offerDraw: 'ड्रॉ का प्रस्ताव दें',
    offerDrawShort: 'ड्रॉ',
    offerDrawConfirm: 'विरोधी को ड्रॉ का प्रस्ताव दें?',
    acceptDrawShort: 'स्वीकार करें',
    resignConfirm: 'हार मानकर गेम छोड़ दें?',
    resign: 'हार मानें',
    resultAria: 'नतीजा',
    rematchOffered: (name) => `${name} ने रीमैच का प्रस्ताव दिया है।`,
    rematchWaiting: 'रीमैच का प्रस्ताव भेजा: विरोधी का इंतज़ार…',
    rematch: 'रीमैच',
    closeResult: 'बोर्ड देखने के लिए बंद करें',
    showResult: 'नतीजा दिखाएँ',
    verdicts: { win: 'जीत', loss: 'हार', draw: 'ड्रॉ' },
    winText: (opponent, gain, rating) => `मैंने Chess-Uno पर ${opponent} को हराया (+${gain} Elo, ${rating})। अब अगला कौन?`,
    shared: 'शेयर हो गया!',
    shareWin: 'मेरी जीत शेयर करें',
  },

  panels: {
    phases: {
      turn_start: 'बारी की शुरुआत',
      draw: 'कार्ड खींचना',
      effect_choice: 'असर चुनना (जोकर)',
      place_pawns: 'वापस मिले प्यादे रखना',
      moves: 'चालें',
      turn_end: 'बारी खत्म',
      game_over: 'गेम खत्म',
    },
    playsArmy: (army) => `${army === 'w' ? 'सफ़ेद' : 'काले'} मोहरों से खेल रहे हैं`,
    chooseMoves: (you) => (you ? 'चुनें कि कितनी चालें चलनी हैं' : 'चालों की संख्या चुन रहे हैं'),
    placePawns: (you, count) => (you ? `${pawns(count)} रखें (हरे खाने)` : `${pawns(count)} रख रहे हैं`),
    joker: (you) => (you ? 'जोकर: कोई भी मोहरा वज़ीर की तरह चलाएँ' : 'जोकर: एक मोहरा वज़ीर की तरह चला रहे हैं'),
    moreMoves: (you, count) => (you ? `${moves(count)} और (या सीरीज़ खत्म करें)` : `${moves(count)} और`),
    playMoves: (you, count) => (you ? `${moves(count)} चलें` : `${moves(count)} चल रहे हैं`),
    pipsAria: (made, total) => `चली गई चालें: ${made} / ${total}`,
    yourTurn: 'आपकी बारी',
    turnOf: (name) => `${name} की बारी`,
    check: 'शह!',
    go: 'शुरू!',
    gameStarts: 'गेम शुरू',
    drawing: (you, summary) => `${you ? 'आपका कार्ड' : 'उनका कार्ड'}: ${summary}`,
    inCheckYou: (mode) => (mode === 'purist' ? 'आपके राजा पर शह है: अपने कार्ड से बचाव करें।' : 'आपके राजा पर हमला है: अपने कार्ड से बचाव करें।'),
    inCheckOpponent: (mode) =>
      mode === 'purist' ? 'विरोधी राजा पर शह: बचाव न हुआ तो शह और मात।' : 'विरोधी राजा पर हमला: बचाव न हुआ तो उसे मार लिया जाएगा।',
    turnNumber: (turn) => `बारी ${turn}`,
    discardTitle: (owner, count) => `${owner} का डिस्कार्ड ढेर: ${n(count, 'कार्ड')}`,
    discard: 'डिस्कार्ड',
    seatAria: (name) => `खिलाड़ी ${name}`,
    connected: 'कनेक्टेड',
    disconnected: 'डिस्कनेक्टेड',
    you: '(आप)',
    ai: 'AI',
    eloTitle: (tier) => `Elo रेटिंग · ${tier}`,
    seatYourTurn: 'आपकी बारी',
    seatPlaying: 'खेल रहे हैं',
    pawnsTitle: 'मारे गए प्यादे, जिन्हें +2 या +4 से वापस पाया जा सकता है',
    toRecover: (count) => `${count} वापस पाने बाकी`,
    turnCardAria: 'इस बारी का कार्ड',
    drawPile: 'गड्डी',
    youDrew: 'आपने खींचा',
    drew: (name) => `${name} ने खींचा`,
    revealed: (label) => `खुला कार्ड: ${label}`,
    noCard: 'कोई कार्ड नहीं',
    jokerChoiceAria: 'चालों की संख्या चुनना',
    jokerChoice: 'जोकर: इस बारी में कितनी चालें?',
    placementHint: (count) => `प्यादा रखने के लिए बोर्ड पर किसी हरे खाने पर क्लिक करें (${count} बाकी)।`,
    endSeries: (count) => `सीरीज़ खत्म करें (${moves(count)} बाकी)`,
    effects: {
      number: (count) => `${moves(count)} चलनी हैं।`,
      zero: 'बारी बिना किसी चाल के निकल गई।',
      skip: 'स्किप: विरोधी तुरंत अगला कार्ड खींचकर उसे खेलता है।',
      reverse: 'रिवर्स: खिलाड़ी अपनी सेनाएँ आपस में बदल लेते हैं; जिसकी बारी है, वह नई सेना से खेलना जारी रखता है।',
      plus: (card, pawnCount, moveCount) => `${card}: ज़्यादा से ज़्यादा ${pawnCount} मारे गए प्यादे वापस, फिर ${moves(moveCount)}।`,
      wild: 'जोकर: एक चाल, जिसमें कोई भी मोहरा वज़ीर की तरह चल सकता है।',
    },
    reveal: {
      who: (you, name) => (you ? 'आपने कार्ड खींचा' : `${name} ने कार्ड खींचा`),
      moves: (count) => moves(count),
      zero: 'बारी छूटी',
      zeroDetail: 'इस बारी में कोई चाल नहीं।',
      skip: 'बारी गई',
      skipDetail: (name) => `अब ${name} की बारी: कार्ड खींचकर खेलेंगे।`,
      reverse: 'रिवर्स!',
      reverseDetail: 'खिलाड़ी अपनी सेनाएँ आपस में बदल लेते हैं।',
      recovers: (count) => `${pawns(count)} वापस`,
      then: (count) => `फिर ${moves(count)}।`,
      oneMove: '1 चाल',
      noPawn: 'वापस पाने के लिए कोई प्यादा नहीं।',
      joker: 'जोकर',
      jokerDetail: 'आपका चुना हुआ कोई भी मोहरा वज़ीर की तरह चलता है।',
    },
    yourKingAttacked: 'आपके राजा पर हमला है',
    enemyKingAttacked: 'विरोधी राजा पर हमला है',
    youWin: 'आपकी जीत',
    wins: (name) => `${name} की जीत`,
    pieces: { p: 'प्यादा', n: 'घोड़ा', b: 'ऊँट', r: 'हाथी', q: 'वज़ीर', k: 'राजा' },
    somePiece: 'मोहरा',
    deliversMate: (piece, from) => `${from} पर खड़ा ${piece} शह और मात देता है`,
    takesKing: (piece, from, to) => `${from} पर खड़ा ${piece} ${to} पर राजा को मारता है`,
    kingTakenOn: (square) => `${square} पर राजा मारा गया`,
    checkmate: 'शह और मात',
    kingCaptured: 'राजा मारा गया',
    statusAria: 'गेम की स्थिति',
    phase: 'चरण',
    actor: 'जिस खिलाड़ी की बारी है',
    movesRemaining: 'बाकी चालें',
    movesValue: (remaining, made) => `${remaining} (चली गईं: ${made})`,
    idleTurns: 'बिना चाल की बारियाँ',
    carefulKing: 'सावधान राजा: राजा ऐसे खाने पर नहीं जा सकता जिस पर हमला हो।',
    kingAttacked: (army, mode) =>
      mode === 'purist'
        ? `${army === 'w' ? 'सफ़ेद' : 'काले'} राजा पर शह है।`
        : `${army === 'w' ? 'सफ़ेद' : 'काले'} राजा पर हमला है (सिर्फ़ जानकारी के लिए, जवाब देना ज़रूरी नहीं)।`,
    youOfferedDraw: 'आपने ड्रॉ का प्रस्ताव दिया है।',
    offersDraw: (name) => `${name} ने ड्रॉ का प्रस्ताव दिया है।`,
    log: 'लॉग',
  },

  board: {
    promotionAria: 'प्रमोशन का चुनाव',
    promotion: 'प्रमोशन:',
    pieces: { q: 'वज़ीर', r: 'हाथी', b: 'ऊँट', n: 'घोड़ा' },
  },

  card: {
    back: 'कार्ड का पिछला हिस्सा',
  },

  tournament: {
    eyebrow: 'टूर्नामेंट',
    hubTitle: 'नॉकआउट टूर्नामेंट',
    hubLead: 'हर हार आपको बाहर कर देती है: आखिर तक टिका रहने वाला खिलाड़ी टूर्नामेंट जीतता है।',
    joinTitle: 'टूर्नामेंट से जुड़ें',
    joinCode: 'टूर्नामेंट कोड',
    joinSubmit: 'जुड़ें',
    createTitle: 'टूर्नामेंट आयोजित करें',
    createHint: 'आपको शेयर करने के लिए एक कोड मिलेगा। सब आ जाएँ, तब आप टूर्नामेंट शुरू करें।',
    name: 'नाम (वैकल्पिक)',
    namePlaceholder: 'शुक्रवार रात का कप',
    modeLabel: 'गेम मोड',
    sizeLabel: 'ज़्यादा से ज़्यादा खिलाड़ी',
    create: 'टूर्नामेंट बनाएँ',
    mine: 'आपके टूर्नामेंट',
    back: 'टूर्नामेंट',
    defaultName: (host) => `${host} का टूर्नामेंट`,
    status: { registration: 'रजिस्ट्रेशन खुला', running: 'जारी है', finished: 'खत्म', cancelled: 'रद्द' },
    playerCount: (count, max) => `${count} / ${max} खिलाड़ी`,
    hostedBy: (name) => `आयोजक: ${name}`,
    open: 'खोलें',
    codeLabel: 'कोड',
    copyLink: 'लिंक कॉपी करें',
    copied: 'कॉपी हो गया!',
    share: 'शेयर करें',
    shared: 'शेयर हो गया!',
    shareText: (name, host) => `${host} ने आपको Chess-Uno पर “${name}” टूर्नामेंट में बुलाया है। जुड़िए:`,
    players: 'खिलाड़ी',
    host: 'आयोजक',
    you: 'आप',
    join: 'टूर्नामेंट से जुड़ें',
    leave: 'टूर्नामेंट छोड़ें',
    start: 'टूर्नामेंट शुरू करें',
    startHint: 'कम से कम 2 खिलाड़ी चाहिए। शुरू करते समय ब्रैकेट रैंडम ढंग से बनता है; उसके बाद कोई नहीं जुड़ सकता।',
    waitingHost: 'आयोजक के शुरू करने का इंतज़ार…',
    cancel: 'टूर्नामेंट रद्द करें',
    cancelConfirm: 'सभी खिलाड़ियों के लिए यह टूर्नामेंट रद्द करें?',
    yourMatch: (opponent) => `${opponent} के खिलाफ़ आपका मैच तैयार है`,
    play: 'खेलें',
    waitingNext: 'अगला मैच: बाकी नतीजों का इंतज़ार…',
    byeThrough: 'आप बिना खेले अगले राउंड में पहुँच गए।',
    eliminated: (round) => `आप ${round} में बाहर हो गए। आगे का हाल यहाँ देखें।`,
    champion: 'टूर्नामेंट विजेता',
    youWon: 'बधाई हो, टूर्नामेंट आपने जीत लिया!',
    cancelled: 'आयोजक ने यह टूर्नामेंट रद्द कर दिया।',
    bracket: 'ब्रैकेट',
    roundName: (fromEnd) =>
      ({ 1: 'फ़ाइनल', 2: 'सेमीफ़ाइनल', 3: 'क्वार्टर फ़ाइनल', 4: 'राउंड ऑफ़ 16', 5: 'राउंड ऑफ़ 32' })[fromEnd] ?? `राउंड ${fromEnd}`,
    roundOne: (fromEnd) =>
      ({ 1: 'फ़ाइनल', 2: 'सेमीफ़ाइनल', 3: 'क्वार्टर फ़ाइनल', 4: 'राउंड ऑफ़ 16', 5: 'राउंड ऑफ़ 32' })[fromEnd] ?? `राउंड ${fromEnd}`,
    tbd: 'तय होना बाकी',
    bye: 'सीधे आगे',
    matchStates: { waiting: 'इंतज़ार में', playing: 'जारी है', done: 'खत्म' },
    endedBy: {
      king_captured: 'राजा मारा गया',
      checkmate: 'शह और मात',
      resign: 'हार मान ली',
      abandon: 'फ़ॉरफ़िट',
      draw_agreed: 'सहमति से ड्रॉ',
      no_move_limit: 'निष्क्रियता से ड्रॉ',
      coin_toss: 'सिक्का उछालकर',
    },
    redraws: (count) => (count === 1 ? '1 ड्रॉ दोबारा खेला गया' : `${count} ड्रॉ दोबारा खेले गए`),
    rulesTitle: 'टूर्नामेंट के नियम',
    rules: (idleMinutes) => [
      'नॉकआउट: मैच हारने वाला बाहर हो जाता है, जीतने वाला अगले राउंड में जाता है।',
      'टूर्नामेंट शुरू होते समय ब्रैकेट रैंडम ढंग से बनता है। अगर खिलाड़ियों की संख्या दो की घात (2, 4, 8, 16…) नहीं है, तो कुछ खिलाड़ी पहले राउंड में बिना खेले आगे बढ़ जाते हैं।',
      'ड्रॉ हुआ गेम रंग बदलकर दोबारा खेला जाता है; तीन ड्रॉ के बाद सिक्का उछालकर फ़ैसला होता है।',
      `रीमैच नहीं होता। जो खिलाड़ी ${n(idleMinutes, 'मिनट')} तक कोई चाल नहीं चलता, या डिस्कनेक्ट रहता है, वह गेम हार जाता है।`,
      'टूर्नामेंट के गेम Elo रैंकिंग में गिने जाते हैं।',
    ],
    notFoundTitle: 'टूर्नामेंट नहीं मिला',
    notFoundLead: 'आयोजक से मिला कोड दोबारा जाँचें।',
    invited: 'आपको एक टूर्नामेंट का न्योता मिला है',
    invitedTo: (name) => (
      <>
        आपको <strong>{name}</strong> टूर्नामेंट का न्योता मिला है
      </>
    ),
    toast: {
      aria: (name) => `टूर्नामेंट मैच: ${name}`,
      ready: (opponent) => (
        <>
          <strong>{opponent}</strong> के खिलाफ़ आपका मैच तैयार है
        </>
      ),
      play: 'खेलें',
      later: 'बाद में',
    },
    game: {
      strip: (name, round) => `${name} · ${round}`,
      rule: (idleMinutes) => `रीमैच नहीं · ज़्यादा से ज़्यादा ${n(idleMinutes, 'मिनट')} की निष्क्रियता`,
      bracket: 'ब्रैकेट देखें',
      backToBracket: 'ब्रैकेट पर वापस',
      drawReplay: 'ड्रॉ: मैच रंग बदलकर दोबारा खेला जाएगा।',
    },
  },

  profile: {
    notFound: 'खिलाड़ी नहीं मिला।',
    eyebrow: 'प्रोफ़ाइल',
    challenge: (name) => `${name} को चुनौती दें`,
    shareMe: (rank, rating) =>
      `Chess-Uno पर ${rank ? `मेरी रैंक #${rank} है और ` : ''}मेरे ${rating} Elo हैं। हिम्मत है तो आकर मुझे गद्दी से उतारो!`,
    shareOther: (name, rating) => `Chess-Uno पर ${name} के ${rating} Elo हैं। कौन देगा टक्कर?`,
    linkCopied: 'लिंक कॉपी हो गया!',
    shared: 'शेयर हो गया!',
    share: 'शेयर करें',
    yourRanking: 'आपकी रैंकिंग',
    ranking: 'रैंकिंग',
    youVs: (name) => `आप बनाम ${name}`,
    draws: (count) => n(count, 'ड्रॉ'),
    headToHead: 'आमने-सामने',
    recentResults: 'ताज़ा नतीजे',
  },

  rules: {
    eyebrow: 'नियमावली',
    toc: 'विषय-सूची',
  },

  legal: {
    updated: (date) => `आखिरी अपडेट: ${date}`,
  },
}
